/**
 * Participants Modal Component
 * Shows all participants in a group conversation
 * Allows admins/managers to add and remove members
 */

import React, { useState, useEffect } from 'react';
import { X, User, UserPlus, UserMinus, Search } from 'lucide-react';
import { Conversation } from '@/types/messaging';
import { userService } from '@/firebase/database';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { messagingService } from '@/firebase/messagingService';
import { fetchUserRoles } from '@/utils/roleHelpers';
import { useNotification } from '@/contexts/NotificationContext';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { doc, getDoc } from 'firebase/firestore';

interface ParticipantInfo {
  id: string;
  name: string;
  email: string;
  picture?: string;
  role?: string;
}

interface ParticipantsModalProps {
  conversation: Conversation;
  isOpen: boolean;
  onClose: () => void;
  onMembersUpdated?: () => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  picture?: string;
}

const ParticipantsModal: React.FC<ParticipantsModalProps> = ({ conversation, isOpen, onClose, onMembersUpdated }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const [participants, setParticipants] = useState<ParticipantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<UserOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserOption[]>([]);
  const [addingMember, setAddingMember] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && conversation.id) {
      loadParticipants();
      checkPermissions();
      
      // Set up real-time listener for conversation participants
      const conversationRef = doc(db, 'conversations', conversation.id);
      const unsubscribe = onSnapshot(conversationRef, (snapshot) => {
        if (snapshot.exists()) {
          // Reload participants when conversation document changes
          loadParticipants();
        }
      }, (error) => {
        console.error('Error listening to conversation changes:', error);
      });
      
      return () => {
        unsubscribe();
      };
    }
  }, [isOpen, conversation.id, user?.id]);

  useEffect(() => {
    if (showAddMemberModal && currentOrganization?.id && conversation.id) {
      loadAvailableUsers();
    }
  }, [showAddMemberModal, currentOrganization?.id, conversation.id]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(availableUsers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = availableUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, availableUsers]);

  const checkPermissions = async () => {
    // Only allow member management for non-direct conversations
    if (!user?.id || !currentOrganization?.id || conversation.type === 'direct') {
      setCanManageMembers(false);
      return;
    }

    try {
      // Check if user is a conversation admin (group admin)
      const participantId = `${conversation.id}_${user.id}`;
      const participantRef = doc(db, 'conversationParticipants', participantId);
      const participantSnap = await getDoc(participantRef);
      
      const isConversationAdmin = participantSnap.exists() && participantSnap.data().role === 'admin';
      
      // Check if user has system role admin or manager using organization-specific roles
      let hasSystemAdminOrManager = false;
      if (!isConversationAdmin) {
        // Get user document to check organization-specific role
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const { getUserSystemRoleForOrganization } = await import('@/utils/roleHelpers');
          const systemRole = getUserSystemRoleForOrganization(userSnap.data(), currentOrganization.id);
          hasSystemAdminOrManager = systemRole === 'admin' || systemRole === 'manager';
          
          // Fallback to legacy role check if organization-specific role not found
          if (!hasSystemAdminOrManager) {
            const userRoles = await fetchUserRoles(user.id);
            hasSystemAdminOrManager = userRoles.isSystemManager;
          }
        }
      }

      setCanManageMembers(isConversationAdmin || hasSystemAdminOrManager);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageMembers(false);
    }
  };

  const loadParticipants = async () => {
    if (!conversation.id) {
      setParticipants([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch the latest conversation from Firestore to get updated participants
      const conversationRef = doc(db, 'conversations', conversation.id);
      const conversationSnap = await getDoc(conversationRef);
      
      let currentParticipants: string[] = [];
      if (conversationSnap.exists()) {
        const conversationData = conversationSnap.data();
        currentParticipants = conversationData.participants || [];
      } else {
        currentParticipants = conversation.participants || [];
      }

      // Also check conversationParticipants collection to ensure we have all members
      // This is important because there might be participants in the collection that aren't in the array yet
      const participantsCollectionRef = collection(db, 'conversationParticipants');
      const participantsQuery = query(
        participantsCollectionRef,
        where('conversationId', '==', conversation.id)
      );
      const participantsSnapshot = await getDocs(participantsQuery);
      
      // Create a Set to merge participants from both sources
      const allParticipantIds = new Set<string>(currentParticipants);
      participantsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.userId) {
          allParticipantIds.add(data.userId);
        }
      });

      // Convert Set back to array
      const mergedParticipants = Array.from(allParticipantIds);

      if (mergedParticipants.length === 0) {
        setParticipants([]);
        setLoading(false);
        return;
      }

      const participantPromises = mergedParticipants.map(async (participantId) => {
        try {
          const userResult = await userService.getUser(participantId);
          if (userResult.success && userResult.data) {
            // Get organization-specific system role
            const { getUserSystemRoleForOrganization } = await import('@/utils/roleHelpers');
            const systemRole = getUserSystemRoleForOrganization(userResult.data, currentOrganization?.id || '');
            
            // Fallback to legacy role only if organization-specific role not found and no organization
            const roleToDisplay = systemRole || (currentOrganization?.id ? undefined : userResult.data.role) || 'user';
            
            return {
              id: participantId,
              name: userResult.data.name || userResult.data.email || 'Unknown User',
              email: userResult.data.email || '',
              picture: userResult.data.picture,
              role: roleToDisplay,
            };
          }
          return null;
        } catch (error) {
          console.error(`Error loading participant ${participantId}:`, error);
          return {
            id: participantId,
            name: 'Unknown User',
            email: '',
            role: 'user',
          };
        }
      });

      const results = await Promise.all(participantPromises);
      // Filter out null values and map to ParticipantInfo
      const validParticipants: ParticipantInfo[] = [];
      for (const result of results) {
        if (result !== null) {
          validParticipants.push({
            id: result.id,
            name: result.name || 'Unknown User',
            email: result.email || '',
            picture: result.picture,
            role: result.role,
          });
        }
      }
      
      // Sort participants: current user first, then alphabetically by name
      validParticipants.sort((a, b) => {
        if (a.id === user?.id) return -1;
        if (b.id === user?.id) return 1;
        return a.name.localeCompare(b.name);
      });

      setParticipants(validParticipants);
    } catch (error) {
      console.error('Error loading participants:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    if (!currentOrganization?.id || !user?.id || !conversation.id) {
      setAvailableUsers([]);
      return;
    }

    try {
      // Fetch the latest conversation from Firestore to get updated participants
      const conversationRef = doc(db, 'conversations', conversation.id);
      const conversationSnap = await getDoc(conversationRef);
      
      let currentParticipants: string[] = [];
      if (conversationSnap.exists()) {
        const conversationData = conversationSnap.data();
        currentParticipants = conversationData.participants || [];
      } else {
        currentParticipants = conversation.participants || [];
      }

      // Get all users in the organization
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('organizationIds', 'array-contains', currentOrganization.id)
      );

      const snapshot = await getDocs(q);
      const usersList: UserOption[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        // Exclude current user and users already in the conversation
        if (doc.id !== user?.id && !currentParticipants.includes(doc.id)) {
          usersList.push({
            id: doc.id,
            name: data.name || data.email || 'Unknown User',
            email: data.email || '',
            picture: data.picture,
          });
        }
      });

      // Also check backward compatibility with organizationId field
      const orgIdQuery = query(
        usersRef,
        where('organizationId', '==', currentOrganization.id)
      );
      const orgIdSnapshot = await getDocs(orgIdQuery);

      orgIdSnapshot.forEach((doc) => {
        if (doc.id !== user?.id && !currentParticipants.includes(doc.id)) {
          const data = doc.data();
          const existingUser = usersList.find(u => u.id === doc.id);
          if (!existingUser) {
            usersList.push({
              id: doc.id,
              name: data.name || data.email || 'Unknown User',
              email: data.email || '',
              picture: data.picture,
            });
          }
        }
      });

      // Remove duplicates and sort
      const uniqueUsers = Array.from(
        new Map(usersList.map(u => [u.id, u])).values()
      ).sort((a, b) => a.name.localeCompare(b.name));

      setAvailableUsers(uniqueUsers);
      setFilteredUsers(uniqueUsers);
    } catch (error: any) {
      console.error('Error loading available users:', error);
      showNotification('Failed to load users', 'error');
    }
  };

  const handleAddMember = async (userId: string) => {
    if (!user?.id || !conversation.id) return;

    setAddingMember(true);
    try {
      const result = await messagingService.addGroupMember(
        conversation.id,
        userId,
        user.id
      );

      if (result.success) {
        showNotification('Member added successfully', 'success');
        setShowAddMemberModal(false);
        setSearchQuery('');
        // Longer delay to ensure Firestore has fully updated and propagated
        await new Promise(resolve => setTimeout(resolve, 500));
        // Reload participants and available users
        await loadParticipants();
        await loadAvailableUsers();
        // Notify parent component
        onMembersUpdated?.();
      } else {
        showNotification(result.error || 'Failed to add member', 'error');
      }
    } catch (error: any) {
      console.error('Error adding member:', error);
      showNotification('Failed to add member', 'error');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!user?.id || !conversation.id) return;

    if (!confirm(`Are you sure you want to remove this member from the group?`)) {
      return;
    }

    setRemovingMember(userId);
    try {
      const result = await messagingService.removeGroupMember(
        conversation.id,
        userId,
        user.id
      );

      if (result.success) {
        showNotification('Member removed successfully', 'success');
        // Small delay to ensure Firestore has updated
        await new Promise(resolve => setTimeout(resolve, 100));
        // Reload participants and available users
        await loadParticipants();
        await loadAvailableUsers();
        // Notify parent component
        onMembersUpdated?.();
      } else {
        showNotification(result.error || 'Failed to remove member', 'error');
      }
    } catch (error: any) {
      console.error('Error removing member:', error);
      showNotification('Failed to remove member', 'error');
    } finally {
      setRemovingMember(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {conversation.type === 'group' ? 'Group Participants' : 
               conversation.type === 'task' ? 'Task Participants' :
               conversation.type === 'contract' ? 'Contract Participants' :
               conversation.type === 'checklist' ? 'Checklist Participants' :
               conversation.type === 'form' ? 'Form Participants' :
               'Participants'}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              {participants.length} {participants.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {canManageMembers && conversation.type !== 'direct' && (
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="p-2 text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
                title="Add Member"
              >
                <UserPlus className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Participants List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : participants.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No participants found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {participant.picture ? (
                      <img
                        src={participant.picture}
                        alt={participant.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-6 h-6 text-gray-600" />
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {participant.name}
                        {participant.id === user?.id && (
                          <span className="ml-2 text-xs text-gray-500">(You)</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 mt-0.5">
                      {participant.email && (
                        <p className="text-xs text-gray-600 truncate">{participant.email}</p>
                      )}
                      {participant.role && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-xs text-gray-500 capitalize">{participant.role}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Remove button for admins/managers (except for current user) */}
                  {canManageMembers && 
                   conversation.type !== 'direct' && 
                   participant.id !== user?.id && (
                    <button
                      onClick={() => handleRemoveMember(participant.id)}
                      disabled={removingMember === participant.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                      title="Remove Member"
                    >
                      {removingMember === participant.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <UserMinus className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add Member</h2>
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSearchQuery('');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Users List */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No users available to add</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((userOption) => (
                    <button
                      key={userOption.id}
                      onClick={() => handleAddMember(userOption.id)}
                      disabled={addingMember}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
                    >
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {userOption.picture ? (
                          <img
                            src={userOption.picture}
                            alt={userOption.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-600" />
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {userOption.name}
                        </p>
                        {userOption.email && (
                          <p className="text-xs text-gray-600 truncate">{userOption.email}</p>
                        )}
                      </div>
                      {addingMember && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantsModal;

